/* ============
 * Router File
 * ============
 *
 * The router, routes and redirects are defined in this file.
 */

import VueRouter from 'vue-router';

import isFunction from 'lodash/isFunction';
import store from '@/store';
import routes from '@/router/routes';
import { injectRouteDefaults, isLoginPage } from '@/router/utils';
import { UnAuthorizedError } from '@/api/interceptors/format-error';
import { LOGIN_INTERNAL_PAGE_NAME, LOGIN_PAGE_NAME } from '@/modules/authentication/config/login-routes';
import { getDefaultLoginPageId } from '@/modules/authentication/config/login-pages';
import { getStaffDefaultSite } from '@/modules/staff/config/homepage';

const router = new VueRouter({
  mode: 'history',
  scrollBehavior(to, from) {
    // check if any matched route config has meta that requires scrolling to top
    if (to.matched.some(m => m.meta.scrollToTop)) {
      return { x: 0, y: 0 };
    }
    // Avoid scrolling if entities are equal
    return to.params.entity === from.params.entity ? null : { x: 0, y: 0 };
  },
  routes,
});

function routeIsNull(route) {
  return route.name === null && (!route.match || route.match.length === 0);
}

// Navigation guards

/**
 * Set the app in the loading state when changing from one route to another.
 */
router.beforeEach(async (to, from, next) => {
  if (to.name !== from.name
    && to.name !== 'default') {
    await store.dispatch('UPDATE_LOADING_VIEW', true);
  }
  next();
});

/**
 * Redirects to the internal login page when accesing /auth directly.
 */
router.beforeEach((to, from, next) => {
  if (to.name === LOGIN_PAGE_NAME) {
    return next({
      name: LOGIN_INTERNAL_PAGE_NAME,
      params: { page: to.params.page || getDefaultLoginPageId() },
      query: to.query,
    });
  }
  return next();
});

/**
 * Loads the base configuration.
 * Redirects the user to the login page if not already logged.
 */
router.beforeEach(async (to, from, next) => {
  await store.dispatch('LOAD_BOOTSTRAP');

  if (isLoginPage(to.name)
    || isLoginPage(from.name)
    || !store.state.loading.app
  ) {
    return next();
  }

  try {
    await store.dispatch('LOAD_BASE_CONFIG', to.params.site);
    return next();
  } catch (e) {
    console.error('Error loading base configuration before router enter', e);
    if (e instanceof UnAuthorizedError) {
      return next({
        name: LOGIN_PAGE_NAME,
        query: {
          redirectTo: to.fullPath,
        },
      });
    }
    return next(false);
  }
});

/**
 * Checks that the given site is a valid one. Sets the default site if none given.
 * Redirects to the not found page if the given site is not recognised.
 */
router.beforeEach(async (to, from, next) => {
  if (!to.matched.length
    || to.matched[0].path !== '/:site') {
    return next();
  }

  // If no site is given, set the default one.
  if (!to.params.site) {
    to.params.site = getStaffDefaultSite();
    return next({ ...to });
  }

  // Check if the given site is valid
  if (!store.getters.getSiteFromIdAlias(to.params.site)) {
    if (routeIsNull(from)) {
      return next({ name: 'notFound', path: to.path });
    }
    return next(false);
  }

  // Check if the given site is allowed for the user.
  if (!store.getters.allowedSites.find(site => site.idAlias === to.params.site)) {
    return next({ name: 'unauthorized', params: { toURL: to.fullPath } });
  }

  return next();
});

/**
 * Checks for feature flags
 * Redirects the user to the unauthorized page if they cannot access the current route.
 */
router.beforeEach(async (to, from, next) => {
  if (!to.meta || !to.meta.feature) {
    return next();
  }
  if (!store.getters.features[to.meta.feature]) {
    // Disabled feature
    return next({ name: 'notFound', path: to.path });
  }
  return next();
});

/**
 * Checks for page permissions.
 * Redirects the user to the unauthorized page if they cannot access the current route.
 */
router.beforeEach(async (to, from, next) => {
  // If is not defined permissions for this route allow route change
  if (!to.meta || !to.meta.permissions) {
    return next();
  }

  // Query for the permissions
  const permissions = to.meta.permissions;
  await store.dispatch('authorization/UPDATE_PERMISSIONS', isFunction(permissions) ? permissions(to.params) : permissions);

  // Can access?
  const access = await store.dispatch('authorization/AUTHORIZE', { action: 'access' });
  if (access) {
    return next();// Authorization granted
  }

  return next({ name: 'unauthorized', params: { toURL: to.fullPath } });
});


/**
 * Watches if the site has changed from the last route, and sets the new one in the store if so.
 */
router.beforeEach(async (to, from, next) => {
  if (typeof to.params.site !== 'undefined'
    && to.params.site !== store.state.site.idAlias) {
    await store.dispatch('SET_SITE', store.getters.getSiteFromIdAlias(to.params.site).id);
    await store.dispatch('bag/searches/REFRESH', null, { root: true });
    next();
  } else {
    next();
  }
});

/**
 * Generate a router location
 * @returns {Object}
 */
export function generateRoute(location) {
  return injectRouteDefaults(location);
}

export default router;
